import { Card } from '@material-ui/core';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { useDispatchAbort, useDispatchAbortPromise } from '@core/hooks/redux';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { addressLookupActions } from '@redux/reducers/common/address-lookup/addressLookupReducer';
import { RootState } from '@redux/reducers/rootReducer';
import {
  AddressDoctorAddress,
  GetAddressDoctorRequestPayload,
  GetLoqateIndexPayload,
  GetLoqateIndexSubPayload,
} from '@redux/types/common/address-lookup/addressLookupTypes';
import { Autocomplete } from '@shared/components/autocomplete';
import { Flex } from '@shared/components/flex';
import { Option } from '@shared/components/select/Select.types';
import { SingleSelect } from '@shared/components/select/single-select';
import { getCountryOptions } from '@shared/constants/options';

import { styles } from './AddressLookup.styles';

const DEFAULT_COUNTRY_OPTION = { label: 'United Kingdom', id: 'GB' };

export interface AddressLookupProps extends WithStyles<typeof styles> {
  search: string;
  onAddressConfirmStep: () => void;
  onManuallyAddress: () => void;
  onSearchChange: (s: string) => void;
}

const AddressLookupComponent: FC<AddressLookupProps> = ({
  classes,
  search,
  onAddressConfirmStep,
  onManuallyAddress,
  onSearchChange,
}) => {
  const addressDoctorIndexAbortRef = useRef<AbortController | null>(null);
  const addressDoctorShowAbortRef = useRef<AbortController | null>(null);
  const loqateIndexAbortRef = useRef<AbortController | null>(null);
  const loqateIndexSubAbortRef = useRef<AbortController | null>(null);
  const loqateShowAbortRef = useRef<AbortController | null>(null);

  const dispatch = useDispatch();
  const dispatchAbortPromise = useDispatchAbortPromise();

  const { user } = useConfig();
  const { constants } = useAgencyConstants();

  const getPreviewAddressAutocomplete = useDispatchAbort<GetLoqateIndexPayload>(
    dispatch,
    addressLookupActions.getPreviewAddressAutocomplete,
    loqateIndexAbortRef
  );
  const getAutocompleteFullAddress = useDispatchAbort<GetLoqateIndexSubPayload>(
    dispatch,
    addressLookupActions.getAutocompleteFullAddress,
    loqateIndexSubAbortRef
  );
  const getAddressDoctorPreviewRequest = useDispatchAbort<GetAddressDoctorRequestPayload>(
    dispatch,
    addressLookupActions.getAddressDoctorPreviewRequest,
    addressDoctorIndexAbortRef
  );

  // Loqate selectors
  const previewsAddresses = useSelector(
    (store: RootState) => store.common?.addressLookup?.addressAutocomplete?.previewAddresses
  );
  const fullPreviewAddresses = useSelector(
    (store: RootState) => store.common?.addressLookup?.addressAutocomplete?.fullPreviewAddresses
  );
  const addressLoading = useSelector((state: RootState) => state.common.addressLookup?.addressAutocomplete.loading);

  // Address doctor selectors
  const addressDoctorAddresses = useSelector(
    (state: RootState) => state.common?.addressLookup?.addressDoctorAutocomplete?.previewAddresses
  );
  const addressDoctorLoading = useSelector(
    (state: RootState) => state.common?.addressLookup?.addressDoctorAutocomplete?.loading
  );

  const countryOptions = useMemo(() => getCountryOptions(constants), [constants]);

  const regionOption: Option = useMemo(() => {
    const regionValue = constants.localisation?.region;
    if (!regionValue) {
      return DEFAULT_COUNTRY_OPTION;
    }

    const countryValue = constants.regionCountryMap[regionValue];
    if (!countryValue) {
      return DEFAULT_COUNTRY_OPTION;
    }

    return countryOptions.find((option) => countryValue === option.id) || DEFAULT_COUNTRY_OPTION;
  }, [constants.countryOptions, constants.localisation, constants.regionCountryMap]);

  const useAddressDoctor = useMemo(() => user.organisation?.settings?.address_doctor === '1', [user.organisation]);
  const useLoqate = useMemo(() => !useAddressDoctor, [useAddressDoctor]);

  useEffect(() => {
    return () => {
      dispatch(addressLookupActions.setAutocompleteFullPreviewAddress([]));
    };
  }, []);

  const clearAddressesList = () => {
    dispatch(addressLookupActions.setAutocompletePreviewAddress([]));
    dispatch(addressLookupActions.setAutocompleteFullPreviewAddress([]));
    dispatch(addressLookupActions.setAutocompleteFinalAddress(undefined));
  };

  const dispatchFullAddress = async (item: any) => {
    if (item?.Text) {
      onSearchChange(item.Text);
    }

    if (item) {
      const editedId = item?.Id.replace(/\|/g, '%7C');

      if (item.Type !== 'Address') {
        getAutocompleteFullAddress({ text: search, id: editedId });
      } else {
        dispatchAbortPromise(addressLookupActions.getAutocompleteFinalAddress, loqateShowAbortRef, {
          id: editedId,
        }).then(() => {
          onSearchChange('');
          onAddressConfirmStep();
        });
      }
    }
  };

  const getAddressDoctorList = (search: string) => {
    getAddressDoctorPreviewRequest({ string: search, country: regionOption.id.toString() });
  };

  const selectAddressDoctor = (address: AddressDoctorAddress) => {
    dispatchAbortPromise(addressLookupActions.getAddressDoctorFullAddressRequest, addressDoctorShowAbortRef, {
      // Address 1
      ...(address.building && { building: address.building }),
      // Address 2
      ...(address.house_number && { house_number: address.house_number }),
      ...(address.street && { street: address.street }),
      ...(address.line2_extra && { line2_extra: address.line2_extra }),
      // Remaining
      ...(address.town && { town: address.town }),
      ...(address.county && { county: address.county }),
      ...(address.postcode && { postcode: address.postcode }),
      ...(address.country && { country: address.country }),
    }).then(() => {
      onSearchChange('');
      onAddressConfirmStep();
      clearAddressDoctorList();
    });
  };

  const clearAddressDoctorList = () => {
    dispatch(addressLookupActions.clearAddressDoctorList());
  };

  const addressDoctorForm = useMemo(() => {
    if (!useAddressDoctor) {
      return null;
    }

    return (
      <Flex autoWidth={false} wrap="nowrap" className={classes.addressContainer}>
        <Autocomplete
          withListUpdate={true}
          list={addressDoctorAddresses}
          loading={addressDoctorLoading}
          loadingText={t('searching_addresses')}
          onSelect={selectAddressDoctor}
          onGetList={getAddressDoctorList}
          minSearchLength={4}
          onClearList={clearAddressDoctorList}
          searchValue={search}
          onSearchValueChange={onSearchChange}
          inputProps={{
            placeholder: t('search_address'),
          }}
          renderOption={(option: any) => {
            const line1 = (option.building as string) || '';
            const line2 = [option.house_number, option.street, option.line2_extra].filter((item) => item).join(' ');

            return [
              line1 ? line1 : line2,
              line1 ? line2 : '',
              option.town,
              option.county,
              option.postcode,
              regionOption.label,
            ]
              .filter((line) => line)
              .join(', ');
          }}
          classes={{
            root: classes.autocompleteAddressDoctor,
            textFieldInput: classes.autocompleteTextField,
          }}
        />
        <SingleSelect
          value={regionOption.id}
          options={[regionOption]}
          classes={{
            root: classes.selectRoot,
            select: classes.select,
            muiOutlined: classes.selectOutlined,
            muiIcon: classes.selectIcon,
            value: classes.selectValue,
          }}
        />
      </Flex>
    );
  }, [addressDoctorAddresses, addressDoctorLoading, classes, onSearchChange, regionOption, search, useAddressDoctor]);

  const loqateForm = useMemo(() => {
    if (!useLoqate) {
      return null;
    }

    return (
      <Autocomplete
        withListUpdate={true}
        list={previewsAddresses}
        priorityList={fullPreviewAddresses}
        loading={addressLoading}
        loadingText={t('searching_addresses')}
        onSelect={dispatchFullAddress}
        onGetList={getPreviewAddressAutocomplete}
        onClearList={clearAddressesList}
        searchValue={search}
        onSearchValueChange={onSearchChange}
        inputProps={{
          placeholder: t('search_address'),
        }}
        renderOption={(option: any) => `${option.Text} ${option.Description}`}
      />
    );
  }, [addressLoading, fullPreviewAddresses, onSearchChange, previewsAddresses, search, useLoqate]);

  return (
    <div className={classes.root}>
      <Card elevation={0} classes={{ root: classes.card }}>
        <div className={classes.headerText}>{t('address_lookup')}</div>
        {loqateForm}
        {addressDoctorForm}
        <div className={classes.textBottom} onClick={onManuallyAddress}>
          {t('trouble_with_the_address_question_enter_manually')}
        </div>
      </Card>
    </div>
  );
};

export const AddressLookup = withStyles(styles)(AddressLookupComponent);
